import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from "@angular/common/http";
import { LeiStorage } from "../models/lei/lei.storage";
import { CabecalhoLeiResponse } from "../arguments/lei/cabecalho.lei.response";
import { AppConfig } from "../app.config";
import {
  CompressedResult,
  CompressionHelper,
} from "../helpers/compression.helper";
import { LeiInfo } from "../models/lei/lei.info";
import { LeiConteudo } from "../models/lei/lei.conteudo";
import { LeiLookup } from "../models/lei/lei.lookup";
import { Lei } from "../models/Lei";
import { StatusService } from "../services/status.service";
import { DataControleRepositorio } from "./dataControle.repositorio";
import { EnumTipoDataControle } from "../enums/EnumDataControle";
import { IdDataLei } from "../models/lei/id.data.lei";
import { ErrorHandlerService } from "../services/errorHandler.service";
import { ErrorLogParameters } from "../models/error/errorLogParameters";
import { StorageService } from "../services/storage.service";
import { UsuarioPreferenciasService } from "../services/data-services/usuario.preferencias.service";
import { first, firstValueFrom, lastValueFrom } from "rxjs";

@Injectable()
export class LeiRepositorio {
  readonly LEI_INFO_STORE = "lei-info";
  readonly LEI_CONTEUDO_STORE = "lei-conteudo";
  readonly LEI_TEMP_STORE = "lei-download";
  readonly databaseName = "svdm";

  constructor(
    private dataControleRepositorio: DataControleRepositorio,
    private statusService: StatusService,
    private httpClient: HttpClient,
    private errorHandlerService: ErrorHandlerService,
    private usuarioPreferenciasService: UsuarioPreferenciasService,
    private storageService: StorageService
  ) {}

  async atualizarLista(): Promise<void> {
    const leisOffline = await this.listarOffline();
    let leisOnline = new Array<LeiStorage>();
    if (this.statusService.getIsAppOnline()) {
      const idLeisOfflineMaiorData = new IdDataLei();
      idLeisOfflineMaiorData.ids = leisOffline.map((lei) => lei.id);
      leisOffline.forEach((l) => {
        const dataLei = l.dataHoraUltimaModificacaoTextoLei
          ? new Date(l.dataHoraUltimaModificacaoTextoLei)
          : null;
        idLeisOfflineMaiorData.MaiorDataAlteracaoApp =
          dataLei && dataLei > idLeisOfflineMaiorData.MaiorDataAlteracaoApp
            ? dataLei
            : idLeisOfflineMaiorData.MaiorDataAlteracaoApp;
      });

      leisOnline = await this.listarOnline(idLeisOfflineMaiorData);
    }
    const promises = new Array<Promise<void>>();
    for (const lei of leisOnline) {
      const leiAtualizar = leisOffline.find((l) => l.id === lei.id);

      if (leiAtualizar) {
        if (
          !leiAtualizar.dataHoraModificacao ||
          lei.dataHoraModificacao > leiAtualizar.dataHoraModificacao
        ) {
          promises.push(this.salvar(lei));
        }
      } else {
        promises.push(this.salvar(lei));
      }
    }
    await Promise.all(promises);

    await this.dataControleRepositorio.salvar({
      data: new Date(),
      tipo: EnumTipoDataControle.DataVerificacaoListaLeis,
      id: EnumTipoDataControle.DataVerificacaoListaLeis.valueOf().toString(),
      dataHoraModificacao: undefined,
      removido: false,
    });
  }

  async carregarLookup(): Promise<LeiLookup[]> {
    try {
      if (await this.canCheckLeis()) {
        await this.atualizarLista();
      }

      const leiStorageCollection = await this.listarOffline();
      const leis = leiStorageCollection.map((lei) =>
        LeiLookup.fromLeiStorage(lei)
      );

      return leis;
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  async carregarLeisPaginada(pagina: number): Promise<LeiLookup[]> {
    try {
      const url = `${AppConfig.apiEndpoint}/Leis/LeisCabecalhosPaginado`;
      let requestParams = new HttpParams();

      requestParams = requestParams.append("pagina", pagina.toString());
      requestParams = requestParams.append(
        "tamanhoPagina",
        AppConfig.tamanhoPagina.toString()
      );

      return await lastValueFrom(
        this.httpClient.get(url, { params: requestParams })
      ).then((leisCabecalhos: LeiLookup[]) => leisCabecalhos);
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  async carregarLeisPesquisaPaginada(
    pagina: number,
    texto: string
  ): Promise<LeiLookup[]> {
    try {
      const url = `${AppConfig.apiEndpoint}/Leis/LeisCabecalhosPesquisaPaginado`;
      let requestParams = new HttpParams();

      requestParams = requestParams.append("texto", texto);
      requestParams = requestParams.append("pagina", pagina.toString());
      requestParams = requestParams.append(
        "tamanhoPagina",
        AppConfig.tamanhoPagina.toString()
      );

      return await lastValueFrom(
        this.httpClient.get(url, { params: requestParams })
      ).then((leisCabecalhos: LeiLookup[]) => leisCabecalhos);
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  async carregarLeisCabecalhoFavoritos(): Promise<LeiLookup[]> {
    try {
      const url = `${AppConfig.apiEndpoint}/Leis/LeisCabecalhosFavoritos`;
      let requestParams = new HttpParams();

      requestParams = requestParams.append(
        "usuarioId",
        this.usuarioPreferenciasService.getConfiguracoesvalue.id
      );

      return await lastValueFrom(
        this.httpClient.get(url, { params: requestParams })
      ).then((leisCabecalhos: LeiLookup[]) => leisCabecalhos);
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  async carregarLei(id: string): Promise<Lei> {
    try {
      const leiStorage = await this.buscarLocal(id);
      const lei = LeiStorage.ToLei(leiStorage);

      return lei;
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  async carregarItemLookup(id: string): Promise<LeiLookup> {
    const leiStorage = await this.buscarLocal(id);

    const itemLookup = LeiLookup.fromLeiStorage(leiStorage);
    itemLookup.baixada = leiStorage.dados ? true : false;

    return itemLookup;
  }

  async baixarLei(id: string): Promise<LeiStorage> {
    try {
      const leiStorage = await this.buscarOnline(id);
      await this.salvar(leiStorage);

      return leiStorage;
    } catch (error) {
      if (
        error instanceof HttpErrorResponse &&
        (<HttpErrorResponse>error).status === 404
      ) {
        await this.removerLei(id);
        return null;
      }
      this.errorHandlerService.handleError(error);
    }
  }

  async removerLei(id: string) {
    await this.storageService.delete(
      this.databaseName,
      this.LEI_INFO_STORE,
      id
    );

    await this.storageService.delete(
      this.databaseName,
      this.LEI_CONTEUDO_STORE,
      id
    );
  }

  private async listarOffline(): Promise<LeiStorage[]> {
    const leis = (
      await this.storageService.list<LeiStorage>(
        this.databaseName,
        this.LEI_INFO_STORE
      )
    ).map((info) => LeiStorage.Load(info, null));
    const temp = this.listarTemp();

    temp.forEach((t) => {
      const iLei = leis.map((l) => l.id).indexOf(t.id);
      if (iLei === -1) {
        leis.push(t);
      } else {
        leis[iLei].disponivel = true;
      }
    });
    return leis;
  }

  private listarTemp(): LeiStorage[] {
    let leis: LeiStorage[] = [];

    const json = localStorage.getItem(this.LEI_TEMP_STORE);
    if (json) {
      leis = JSON.parse(json);
    }

    return leis.map((l) => LeiStorage.Load(l, null));
  }

  private async listarOnline(idsLeisOffline: IdDataLei): Promise<LeiStorage[]> {
    const url = `${AppConfig.apiEndpoint}/leis/cabecalhos`;

    try {
      idsLeisOffline = !idsLeisOffline ? new IdDataLei() : idsLeisOffline;

      const result = <CompressedResult>(
        await firstValueFrom(
          this.httpClient.post(url, idsLeisOffline).pipe(first())
        )
      );

      const json = CompressionHelper.unzip(result);
      const leis = (<CabecalhoLeiResponse[]>JSON.parse(json)).map((l) =>
        CabecalhoLeiResponse.toLeiStorage(l)
      );
      return leis;
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  private async salvar(lei: LeiStorage): Promise<void> {
    try {
      await this.storageService.upsert(
        LeiInfo.fromStorage(lei),
        this.databaseName,
        this.LEI_INFO_STORE
      );

      if (lei.dados) {
        await this.storageService.upsert(
          LeiConteudo.fromStorage(lei),
          this.databaseName,
          this.LEI_CONTEUDO_STORE
        );
      }
    } catch (error) {
      this.errorHandlerService.handleError(error);
    }
  }

  private async buscarLocal(id: string): Promise<LeiStorage> {
    const leiInfo = await this.storageService.getByKey<LeiStorage>(
      id,
      this.databaseName,
      this.LEI_INFO_STORE
    );
    const leiConteudo = await this.storageService.getByKey<LeiStorage>(
      id,
      this.databaseName,
      this.LEI_CONTEUDO_STORE
    );

    if (
      leiInfo &&
      leiConteudo &&
      leiInfo.dataHoraUltimaModificacaoTextoLei ===
        leiConteudo.dataHoraUltimaModificacaoTextoLei
    ) {
      return LeiStorage.Load(leiInfo, leiConteudo);
    } else if (this.statusService.getIsAppOnline()) {
      const lei = await this.baixarLei(id);
      if (lei) {
        return lei;
      } else {
        return null;
      }
    }
  }

  private async buscarOnline(id: string): Promise<LeiStorage> {
    const url = `${AppConfig.apiEndpoint}/leis/conteudo/${id}`;

    try {
      const result = <CompressedResult>(
        await firstValueFrom(this.httpClient.get(url).pipe(first()))
      );

      const lei = <Lei>JSON.parse(CompressionHelper.unzip(result));
      const leiStorage = LeiStorage.FromLei(lei);

      return leiStorage;
    } catch (error) {
      const params = new Array<ErrorLogParameters>();
      params.push({ name: "urlEndpoint", value: url });
      this.errorHandlerService.handleError(error);
    }
  }

  private async canCheckLeis(): Promise<boolean> {
    if (!this.statusService.getIsAppOnline()) {
      return false;
    }

    const today = new Date();
    const lastDocumentsListCheck = await this.dataControleRepositorio.buscar(
      EnumTipoDataControle.DataVerificacaoListaLeis
    );
    const outdated =
      lastDocumentsListCheck &&
      lastDocumentsListCheck.data.getDay() === today.getDay() &&
      lastDocumentsListCheck.data.getMonth() === today.getMonth() &&
      lastDocumentsListCheck.data.getFullYear() === today.getFullYear()
        ? true
        : false;

    const localDbIsEmpty =
      (
        await this.storageService.list<LeiStorage>(
          this.databaseName,
          this.LEI_INFO_STORE
        )
      ).length === 0;

    return (outdated || localDbIsEmpty) && this.statusService.getIsAppOnline();
  }
}
